import { useCallback, useMemo } from 'react';
import { Box, Center, SimpleGrid, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { useMask } from '@react-input/mask';

import { Button, DatePicker, Select, TextInput } from 'components';
import { CardRequestStep } from 'types';
import { AppConstants } from '../../../constants';
import { AppUtils } from 'utils';

const { MASK_SWISS_PHONE_NUMBER, PHONE_NUMBER_CODES, PHONE_NUMBER_DATA, PHONE_NUMBER_CODE_SWITZERLAND } = AppConstants;

const PersonalInformationStep = ({
  form,
  onSubmit,
  options,
  t,
}: CardRequestStep) => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const onSubmitWrapper = useCallback(() => {
    if (!isFormValid()) {
      return;
    }
    onSubmit();
  }, [form.values, options]);

  const isFormValid = useCallback(() => {
    const validations: {
      field: string;
      condition: boolean;
      error: string
    }[] = [
        { field: 'firstName', condition: !form.values.firstName, error: t('input.required') },
        { field: 'lastName', condition: !form.values.lastName, error: t('input.required') },
        { field: 'phoneNumber', condition: !form.values.phoneNumber, error: t('input.required') },
        { field: 'phoneNumber', condition: form.values.phoneNumber !== undefined && !PHONE_NUMBER_CODES.some(code => form.values.phoneNumber.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(form.values.phoneNumber.replace(/\s/g, '').length)), error: t('input.phoneNumber.invalid') },
        { field: 'phoneNumber', condition: form.values.phoneNumber.startsWith(PHONE_NUMBER_CODE_SWITZERLAND) && !form.values.phoneNumber.startsWith(PHONE_NUMBER_CODE_SWITZERLAND + ' 7') && form.values.phoneNumber.length > 3, error: t('input.phoneNumber.swissStartWith') },
        { field: 'email', condition: !form.values.email, error: t('input.required') },
        { field: 'email', condition: form.values.email !== undefined && !/^\S+@\S+$/.test(form.values.email), error: t('input.email.invalid') },
        { field: 'gender', condition: !form.values.gender, error: t('input.required') },
      ];

    let isValid = true;
    validations.forEach(({ field, condition, error }) => {
      if (condition) {
        form.setFieldError(field, error);
        isValid = false;
      }
    });

    return isValid;
  }, [form.values, options]);

  const nationalities = useMemo(() => AppUtils.getOrderedCountryList(options['nationality'], t), [options, t])

  return (
    <Center>
      <Box w={'80%'}>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Stack>
            <TextInput
              label={t('input.firstName.label')}
              placeholder={t('input.firstName.placeholder')}
              key={form.key('firstName')}
              {...form.getInputProps('firstName')}
              required={true}
            />
            <TextInput
              label={t('input.lastName.label')}
              placeholder={t('input.lastName.placeholder')}
              key={form.key('lastName')}
              {...form.getInputProps('lastName')}
              required={true}
            />
            <TextInput
              label={t('input.phoneNumber.label')}
              placeholder={t('input.phoneNumber.placeholder')}
              type={'tel'}
              {...form.getInputProps('phoneNumber')}
              key={form.key('phoneNumber')}
              ref={phoneNumberRef}
              onBeforeInput={(event: any) => {
                const cursorPos = event.currentTarget.selectionStart || 0;

                console.log(event.currentTarget)
                if (cursorPos === 0) {
                  const inputValue = (event as any).data;
                  if (inputValue && inputValue !== '7') {
                    event.preventDefault();
                  }
                }
              }}
              required={true}
            />
            <TextInput
              label={t('input.email.label')}
              placeholder={t('input.email.placeholder')}
              key={form.key('email')}
              {...form.getInputProps('email')}
              required={true}
            />
          </Stack>
          <Stack>
            <DatePicker
              label={t('input.birthDate.label')}
              placeholder={t('input.date.placeholder')}
              key={form.key('birthDate')}
              {...form.getInputProps('birthDate')}
              minDate={dayjs().subtract(65, 'years').toDate()}
              maxDate={dayjs().subtract(18, 'years').toDate()}
            />
            <Select
              label={t('input.gender.label')}
              data={['MALE', 'FEMALE', 'OTHER'].map(el => (
                { value: el, label: t(`input.gender.options.${el}`) }
              ))}
              key={form.key('gender')}
              {...form.getInputProps('gender')}
              clearable={true}
              required={true}
            />
            <Select
              label={t('input.nationality.label')}
              data={nationalities}
              key={form.key('nationality')}
              {...form.getInputProps('nationality')}
              clearable={true}
            />
            {form.values.nationality && form.values.nationality !== 'SWITZERLAND' && form.values.nationality !== 'LIECHTENSTEIN' ?
              <>
                <Select
                  label={t('input.visaType.label')}
                  data={options['visaType'].map(el => (
                    { value: el, label: t(`input.visaType.options.${el}`) }
                  ))}
                  key={form.key('visaType')}
                  {...form.getInputProps('visaType')}
                  clearable={true}
                />
              </>
              : undefined}
          </Stack>
        </SimpleGrid>
        <Button mt={'lg'} onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Box>
    </Center>
  )
}

export default PersonalInformationStep;